import { gql, useQuery } from '@apollo/client';

import type {
  GetOnboardingVehiclesQuery,
  GetOnboardingVehiclesQueryVariables,
} from './__generated__/useOnboardingVehicles.gql';
import type { VehicleOwnership } from '../../__generated__/globalTypes';

const GET_ONBOARDING_VEHICLES = gql`
  query GetOnboardingVehicles {
    getCurrentSession {
      id
      user {
        id
        family {
          id
          vehicles {
            id
            ownership
            ownershipOther
          }
        }
      }
    }
  }
`;

export interface IOnboardingVehicle {
  id: string;
  ownership: VehicleOwnership;
  ownershipOther: string | null;
}

export interface IUseOnboardingVehiclesResult {
  /** Family-scoped vehicles in API order — `VehicleController.getAllByFamilyId` sorts by `createdAt`, `id`. */
  vehicles: IOnboardingVehicle[];
  loading: boolean;
}

/**
 * Reads the acting user's family vehicles for `DependantVehicleStep`'s
 * pre-fill on load. Used alongside `useUpsertVehicle` + `useDeleteVehicle`
 * to round-trip the form on Continue.
 */
export const useOnboardingVehicles = (): IUseOnboardingVehiclesResult => {
  const { data, loading } = useQuery<GetOnboardingVehiclesQuery, GetOnboardingVehiclesQueryVariables>(
    GET_ONBOARDING_VEHICLES,
    { fetchPolicy: 'cache-and-network' }
  );

  const family = data?.getCurrentSession?.user?.family ?? null;
  if (!family) {
    return { vehicles: [], loading };
  }

  const vehicles: IOnboardingVehicle[] = family.vehicles.map((v) => ({
    id: v.id,
    ownership: v.ownership,
    ownershipOther: v.ownershipOther ?? null,
  }));

  return { vehicles, loading };
};
